<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CatalogAPI from 'dashboard/api/catalog';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const products = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const currentPage = ref(1);
const total = ref(0);
const perPage = 10;
const debounceTimer = ref(null);

const selectedIds = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
});

const allPageSelected = computed(
  () => products.value.length > 0 && products.value.every(p => selectedIds.value.includes(p.id))
);

const fetchProducts = async (page = 1) => {
  try {
    loading.value = true;
    const { data } = await CatalogAPI.getProducts({ query: searchQuery.value, page, status: 'active', perPage: 10 });
    products.value = data.products || [];
    currentPage.value = data.page || page;
    total.value = data.total || 0;
  } finally {
    loading.value = false;
  }
};

onMounted(() => fetchProducts(1));

watch(searchQuery, () => {
  clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => fetchProducts(1), 300);
});

const toggleProduct = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(i => i !== id)
    : [...selectedIds.value, id];
};

const toggleAll = () => {
  const pageIds = products.value.map(p => p.id);
  if (allPageSelected.value) {
    selectedIds.value = selectedIds.value.filter(id => !pageIds.includes(id));
  } else {
    selectedIds.value = [...new Set([...selectedIds.value, ...pageIds])];
  }
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center gap-3">
      <Input
        v-model="searchQuery"
        type="search"
        :placeholder="t('CATALOG.PRODUCTS.SEARCH_PLACEHOLDER')"
        :custom-input-class="['h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 ltr:!pl-8 !py-1']"
        class="w-72"
      >
        <template #prefix>
          <Icon icon="i-lucide-search" class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2" />
        </template>
      </Input>
      <span v-if="selectedIds.length" class="text-xs text-n-slate-10">
        {{ selectedIds.length }} {{ t('CATALOG.PRODUCT_SETS.FORM.SELECTED') }}
      </span>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <Spinner />
    </div>

    <template v-else>
      <div class="border border-n-weak rounded-xl overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-n-solid-2 border-b border-n-weak">
            <tr class="text-n-slate-10 text-xs">
              <th class="w-10 px-4 py-3 text-left">
                <input type="checkbox" :checked="allPageSelected" class="rounded" @change="toggleAll" />
              </th>
              <th class="px-3 py-3 text-left font-medium w-10" />
              <th class="px-3 py-3 text-left font-medium">{{ t('CATALOG.PRODUCTS.VARIANT_HEADERS.TITLE') }}</th>
              <th class="px-3 py-3 text-left font-medium">{{ t('CATALOG.PRODUCTS.FILTER.SOURCE_LABEL') }}</th>
              <th class="px-3 py-3 text-left font-medium">{{ t('CATALOG.PRODUCTS.FILTER.STATUS_LABEL') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="product in products"
              :key="product.id"
              class="hover:bg-n-alpha-1 cursor-pointer transition-colors"
              @click="toggleProduct(product.id)"
            >
              <td class="px-4 py-3">
                <input
                  type="checkbox"
                  :checked="selectedIds.includes(product.id)"
                  class="rounded"
                  @click.stop
                  @change="toggleProduct(product.id)"
                />
              </td>
              <td class="px-3 py-3">
                <img v-if="product.image_url" :src="product.image_url" :alt="product.title" class="size-8 rounded object-cover" />
                <div v-else class="size-8 rounded bg-n-solid-3 flex items-center justify-center">
                  <i class="i-lucide-image text-n-slate-9 text-xs" />
                </div>
              </td>
              <td class="px-3 py-3 text-n-slate-12 font-medium">{{ product.title }}</td>
              <td class="px-3 py-3 text-n-slate-10 text-xs">{{ product.source?.kind }}</td>
              <td class="px-3 py-3">
                <span class="text-[10px] px-2 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11">{{ product.status }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="[&>div]:!border-t-0">
        <PaginationFooter
          v-if="total > perPage"
          :current-page="currentPage"
          :total-items="total"
          :items-per-page="perPage"
          @update:current-page="fetchProducts"
        />
      </div>
    </template>
  </div>
</template>
